import React, { useState, useEffect, useCallback, useRef } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';


function AVSearch() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(true); // Add a new state variable for login status
  const [settings, setSettings] = useState({});

  const publicUrl = process.env.REACT_APP_PUBLIC_URL || 'http://localhost:5010';
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5011';


  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${backendUrl}/api/settings`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setSettings(data);
        } else {
          console.error('Error fetching settings:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSettings();
  }, [backendUrl]);


  const [isSearchButtonDisabled, setIsSearchButtonDisabled] = useState(true);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [searchSources, setSearchSources] = useState({
    gdrive: true,
    gmail: true,
    wiki: false,
  });
  const isInitialMount = useRef(true);
  const typingTimeoutRef = useRef(null);

  // Function to check if the search button should be enabled
  const shouldEnableSearchButton = useCallback(
    (currentSources = searchSources) => {
      const isQueryValid = searchQuery.trim().length >= 2;
      const isSourceSelected = Object.values(currentSources).some(
        (source) => source
      );
      return isQueryValid && isSourceSelected;
    },
    [searchQuery, searchSources]
  );

  const handleSearchSubmit = useCallback(async (newPageToken = null) => {
    const isButtonEnabled = shouldEnableSearchButton();
    if (isSearchButtonDisabled || !isButtonEnabled) return;

    setIsLoading(true);
    setSearchResults([]);
    try {
      const trimmedQuery = searchQuery.trim();
      const selectedSources = Object.keys(searchSources).filter((key) => searchSources[key]);
      const response = await fetch(
        `${backendUrl}/api/search?query=${trimmedQuery}&pageToken=${newPageToken || ''}&sources=${selectedSources.join(',')}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.results);
        setNextPageToken(data.nextPageToken);
        if (newPageToken === null) {
          setCurrentPageIndex(0);
        } else {
          setCurrentPageIndex(currentPageIndex + 1);
        }
      } else if (response.status === 401) {
        // Handle 401 Unauthorized error
        console.error('Unauthorized: Session expired or invalid.');
        setIsLoggedIn(false); // Update login state
        // Redirect to Google login page
        window.location.href = `${publicUrl}`;
      } else {
        setSearchResults([]);
        setNextPageToken(null);
        setCurrentPageIndex(0);
      }
    } catch (error) {
      console.error('Error during search:', error);
      setSearchResults([]);
      setNextPageToken(null);
      setCurrentPageIndex(0);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, searchSources, isSearchButtonDisabled, backendUrl, publicUrl, currentPageIndex, shouldEnableSearchButton]);

  // New useEffect to trigger search when searchSources changes
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    // handleSearchSubmit();
  }, [searchSources, handleSearchSubmit]);

  useEffect(() => {
    setIsSearchButtonDisabled(!shouldEnableSearchButton());
  }, [shouldEnableSearchButton]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    // Clear any existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !isSearchButtonDisabled) {
      handleSearchSubmit();
    }
  };

  const formatFileSize = (size) => {
    if (!size) return '-';
    const kb = size / 1024;
    if (kb < 1024) {
      return `${kb.toFixed(1)} kB`;
    } else {
      return `${(kb / 1024).toFixed(1)} MB`;
    }
  };

  const formatModifiedTime = (modifiedTime) => {
    if (!modifiedTime) return '-';
    return new Date(modifiedTime).toLocaleString();
  };

  const handleNextResultsClick = () => {
    handleSearchSubmit(nextPageToken);
  };

  const handleSourceChange = (event) => {
    setSearchSources(
      (prevSources) => {
        return {
          ...prevSources,
          [event.target.name]: event.target.checked,
        };
      } 
    );
  };

   // Update searchSources.wiki when settings.outlineApiKey changes
  useEffect(() => {
    setSearchSources(prevSources => ({
      ...prevSources,
      wiki: !!settings.outlineApiKey,
    }));
  }, [settings.outlineApiKey]);

  return (
    <Box sx={{width: '100%',display: 'flex', flexDirection: 'column', alignContent: 'left' }}>
      
    {!isLoggedIn && (
        <Box mt={2} >
          <Typography variant="body2">
            Your session has expired. Please <a href={`${publicUrl}/login`}>log in</a> again.
          </Typography>
        </Box>
      )}

      <Box mt={2} display="flex" alignItems="left">
        <FormControlLabel
          control={
            <Checkbox
              checked={searchSources.gdrive}
              onChange={handleSourceChange}
              name="gdrive"
              color="primary"
            />
          }
          label="GDrive"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={searchSources.gmail}
              onChange={handleSourceChange}
              name="gmail"
              color="primary"
            />
          }
          label="GMail"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={searchSources.wiki}
              disabled={!settings.outlineApiKey} // Disable the toggle based on the state
              onChange={handleSourceChange}
              name="wiki"
              color="primary"
            />
          }
          label="Wiki"
        />
      </Box>
      <Box mt={2} display="flex" alignItems="left" >
        <TextField
          label="Results are based on your individual access rights... Multi-page results for single source selection."
          variant="outlined"
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyDown={handleKeyPress}
          sx={{ mr: 0, width: '100%' }}
        />
      </Box>
      <Box mt={2} display="flex" alignItems="left" width={'100%'} >
        <Button variant="contained" onClick={() => handleSearchSubmit()} disabled={isLoading || isSearchButtonDisabled}>
          Find
        </Button>
        {nextPageToken && (
                <Box flexGrow={1} display="flex" justifyContent="flex-end">

            <Button  variant="contained" onClick={handleNextResultsClick}>
              More...
            </Button>
            </Box>
        )}
      </Box>
      {isLoading && (
        <Box mt={0} display="flex" justifyContent="left">
          <CircularProgress />
        </Box>
      )}
      {!isLoading && searchResults.length > 0 && (
        <Box mt={2}>
          <Table style={{ tableLayout: 'auto', borderCollapse: 'collapse' }}>
            <TableHead>
              <TableRow>
                <TableCell width="35px" sx={{ padding: '2px', fontWeight: 'bold' }}>
                  Type
                </TableCell>
                <TableCell sx={{ 
                  padding: '2px', 
                  fontWeight: 'bold', 
                  minWidth: '300px', // Ensure the column is at least 30px wide
                  flexGrow: 1,       // Allow the column to grow and take up available space
                  flexShrink: 1,     // Allow the column to shrink if necessary
                  overflow: 'hidden', // Hide overflowing content
                  textOverflow: 'ellipsis', // Add ellipsis for overflowing text
                  whiteSpace: 'nowrap', // Prevent text from wrapping
                  }}>
                  File Name
                </TableCell>
                <TableCell width="200px" sx={{ padding: '2px', fontWeight: 'bold' }}>
                  Folder
                </TableCell>
                <TableCell width="150px" sx={{ padding: '2px', fontWeight: 'bold' }}>
                  Changed by
                </TableCell>
                <TableCell width="150px" sx={{ padding: '2px', fontWeight: 'bold' }}>
                  Last Modified Time
                </TableCell>
                <TableCell width="80px" sx={{ padding: '2px', fontWeight: 'bold' }}>
                  File Size
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {searchResults.map((file, index) => (
                <TableRow key={`${file.id}-${index}`}>
                  <TableCell sx={{ padding: '2px' }}>
                    {file.iconURL && <img src={file.iconURL} alt="icon" style={{ height: '24px', width: '24px' }} />}
                  </TableCell>
                  <TableCell sx={{ padding: '2px', overflowWrap: 'break-word', wordBreak: 'break-word', maxWidth: '200px' }}>
                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <a href={file.titleURL} target="_blank" rel="noopener noreferrer">
                        {file.titleName}
                      </a>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ padding: '2px' }}>
                    {file.folderName ? (
                      <Box>
                        {file.folderURL ? (
                          <a href={file.folderURL} target="_blank" rel="noopener noreferrer">
                            {file.folderName}
                          </a>
                        ) : (
                          file.folderName
                        )}
                      </Box>
                    ) : '-'}
                  </TableCell>
                  <TableCell sx={{ padding: '2px' }}>{file.owner ? file.owner : '-'}</TableCell>
                  <TableCell sx={{ padding: '2px' }}>{formatModifiedTime(file.lastChanged)}</TableCell>
                  <TableCell sx={{ padding: '2px' }}>{formatFileSize(file.size)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      {!isLoading && searchResults.length === 0 && (
        <Box mt={2}>
          <Typography variant="body2">No results found.</Typography>
        </Box>
      )}
    </Box>
  );
}

export default AVSearch;
